import type { SkyChatSocketClient } from '@skychat/shared-core';
import type { Socket } from 'socket.io-client';

export type MobileConnectionState =
  | 'idle'
  | 'connecting'
  | 'authenticating'
  | 'ready'
  | 'recovering'
  | 'suspended'
  | 'offline'
  | 'stopped';

export interface MobileConnectionSnapshot {
  state: MobileConnectionState;
  transportEpoch: number;
  authenticatedEpoch: number;
  sessionKey: string;
  reason: string;
}

interface ReadyWaiter {
  resolve: (client: SkyChatSocketClient) => void;
  reject: (error: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

export interface MobileAuthenticationProof {
  transportEpoch?: number;
  socketId?: string;
}

export interface MobileConnectionCoordinatorOptions {
  getClient: () => SkyChatSocketClient | null;
  getToken: () => string | null;
  getForeground: () => boolean;
  getOnline: () => boolean;
  deviceKind: string;
  deviceSessionId: string;
  authFallbackDelayMs?: number;
  reconnectBaseDelayMs?: number;
  reconnectMaxDelayMs?: number;
  onStateChange?: (snapshot: MobileConnectionSnapshot) => void;
  onReady?: (client: SkyChatSocketClient, snapshot: MobileConnectionSnapshot) => void;
  onUnavailable?: (snapshot: MobileConnectionSnapshot) => void;
}

/**
 * Owns the mobile realtime transport lifecycle.
 *
 * Socket.IO can keep the same Socket object while replacing the underlying
 * transport after a resume. Readiness is therefore tied to a monotonically
 * increasing transport epoch rather than to object identity alone.
 */
export class MobileConnectionCoordinator {
  private readonly options: MobileConnectionCoordinatorOptions;
  private state: MobileConnectionState = 'idle';
  private reason = 'init';
  private transportEpoch = 0;
  private authenticatedEpoch = -1;
  private authAttempt = 0;
  private reconnectAttempt = 0;
  private authTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private freshReconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private stopped = false;
  private suspended = false;
  private lastResumeAt = 0;
  private waiters = new Set<ReadyWaiter>();

  constructor(options: MobileConnectionCoordinatorOptions) {
    this.options = options;
  }

  start(reason = 'start'): void {
    if (this.stopped) return;
    const client = this.options.getClient();
    if (client?.socket.connected) this.handleTransportConnect(client.socket, `${reason}:already-connected`);
    else this.ensureReady(reason);
  }

  stop(reason = 'stop'): void {
    if (this.stopped) return;
    this.stopped = true;
    this.suspended = false;
    this.clearTimers();
    this.authenticatedEpoch = -1;
    this.setState('stopped', reason);
    const error = new Error('Realtime connection stopped');
    for (const waiter of this.waiters) {
      clearTimeout(waiter.timer);
      waiter.reject(error);
    }
    this.waiters.clear();
  }

  getSnapshot(): MobileConnectionSnapshot {
    return {
      state: this.state,
      transportEpoch: this.transportEpoch,
      authenticatedEpoch: this.authenticatedEpoch,
      sessionKey: this.sessionKey(),
      reason: this.reason
    };
  }

  isReady(client: SkyChatSocketClient | null = this.options.getClient()): boolean {
    return !!client
      && !this.stopped
      && !this.suspended
      && client.socket.connected
      && this.state === 'ready'
      && this.authenticatedEpoch === this.transportEpoch;
  }

  sessionKey(): string {
    const socketId = String(this.options.getClient()?.socket.id || 'disconnected');
    return `${this.transportEpoch}:${socketId}`;
  }

  ensureReady(reason = 'ensure-ready'): void {
    if (this.stopped || this.suspended) return;
    if (!this.options.getOnline()) {
      this.invalidateAuthentication(`${reason}:offline`);
      this.setState('offline', reason);
      return;
    }
    if (!this.options.getForeground()) {
      this.suspend(`${reason}:background`);
      return;
    }

    const client = this.options.getClient();
    if (!client) {
      this.setState('idle', `${reason}:missing-client`);
      return;
    }
    if (this.isReady(client)) return;
    if (!client.socket.connected) {
      this.setState(this.transportEpoch > 0 ? 'recovering' : 'connecting', reason);
      try { client.connect(); } catch { this.scheduleReconnect(`${reason}:connect-error`); }
      this.scheduleReconnect(`${reason}:connect-wait`);
      return;
    }

    this.setState('authenticating', reason);
    this.scheduleAuthentication(client, reason, 0);
  }

  waitUntilReady(timeoutMs = 12000, reason = 'wait-until-ready'): Promise<SkyChatSocketClient> {
    if (this.stopped) return Promise.reject(new Error('Realtime connection stopped'));
    const client = this.options.getClient();
    if (client && this.isReady(client)) return Promise.resolve(client);
    this.ensureReady(reason);

    return new Promise<SkyChatSocketClient>((resolve, reject) => {
      let waiter!: ReadyWaiter;
      waiter = {
        resolve,
        reject,
        timer: setTimeout(() => {
          this.waiters.delete(waiter);
          reject(new Error('Realtime connection is not ready'));
        }, Math.max(250, timeoutMs))
      };
      this.waiters.add(waiter);
    });
  }

  handleTransportConnect(socket: Socket, reason = 'transport-connect'): void {
    const client = this.options.getClient();
    if (this.stopped || !client || client.socket !== socket) return;
    if (this.suspended || !this.options.getForeground()) {
      try { client.disconnect(); } catch { }
      return;
    }
    if (!this.options.getOnline()) {
      this.setState('offline', reason);
      return;
    }

    this.transportEpoch += 1;
    this.authenticatedEpoch = -1;
    this.authAttempt = 0;
    this.reconnectAttempt = 0;
    this.clearAuthTimer();
    this.clearReconnectTimer();
    this.setState('authenticating', reason);

    // createSkyChatSocket already emits one auth packet from its own connect
    // listener. AppContext patches that packet with the messageSyncV2 capability
    // before connection; this is only the acknowledged fallback.
    this.scheduleAuthentication(
      client,
      reason,
      this.options.authFallbackDelayMs ?? 700
    );
  }

  confirmAuthenticated(
    socket: Socket,
    reason = 'auth-confirmed',
    proof: MobileAuthenticationProof = {}
  ): boolean {
    const client = this.options.getClient();
    if (this.stopped || this.suspended || !client || client.socket !== socket || !socket.connected) return false;
    if (proof.transportEpoch !== undefined && proof.transportEpoch !== this.transportEpoch) return false;
    if (proof.socketId && proof.socketId !== String(socket.id || '')) return false;
    if (this.transportEpoch === 0) this.transportEpoch = 1;
    const alreadyReady = this.authenticatedEpoch === this.transportEpoch && this.state === 'ready';
    this.authenticatedEpoch = this.transportEpoch;
    this.authAttempt = 0;
    this.reconnectAttempt = 0;
    this.clearAuthTimer();
    this.clearReconnectTimer();
    this.setState('ready', reason);

    if (!alreadyReady) {
      for (const waiter of this.waiters) {
        clearTimeout(waiter.timer);
        waiter.resolve(client);
      }
      this.waiters.clear();
      this.options.onReady?.(client, this.getSnapshot());
    }
    return !alreadyReady;
  }

  handleAuthError(socket: Socket, reason = 'auth-error'): void {
    const client = this.options.getClient();
    if (!client || client.socket !== socket) return;
    this.invalidateAuthentication(reason);
    if (!this.stopped && !this.suspended && socket.connected && this.options.getForeground() && this.options.getOnline()) {
      this.setState('authenticating', reason);
      this.scheduleAuthentication(client, reason, this.nextAuthDelay());
    }
  }

  handleTransportDisconnect(socket: Socket, reason = 'transport-disconnect'): void {
    const client = this.options.getClient();
    if (!client || client.socket !== socket) return;
    this.invalidateAuthentication(reason);
    if (this.stopped) return;
    if (this.suspended || !this.options.getForeground()) {
      this.setState('suspended', reason);
      return;
    }
    if (!this.options.getOnline()) {
      this.setState('offline', reason);
      return;
    }
    this.setState('recovering', reason);
    this.scheduleReconnect(reason);
  }

  invalidateAuthentication(reason = 'invalidate-auth'): void {
    this.authenticatedEpoch = -1;
    this.clearAuthTimer();
    this.reason = reason;
  }

  suspend(reason = 'background'): void {
    if (this.stopped || this.suspended) return;
    this.suspended = true;
    this.invalidateAuthentication(reason);
    this.clearReconnectTimer();
    this.clearFreshReconnectTimer();
    this.setState('suspended', reason);
    const client = this.options.getClient();
    if (client?.socket.connected) {
      try { client.disconnect(); } catch { }
    }
    this.options.onUnavailable?.(this.getSnapshot());
  }

  resume(reason = 'foreground'): void {
    if (this.stopped) return;
    const now = Date.now();
    const wasSuspended = this.suspended;
    const duplicateResume = !wasSuspended && now - this.lastResumeAt < 250;
    this.lastResumeAt = now;
    this.suspended = false;
    if (!this.options.getOnline()) {
      this.setState('offline', reason);
      return;
    }
    if (!this.options.getForeground()) return;

    const client = this.options.getClient();
    if (!wasSuspended && this.isReady(client)) return;
    if (duplicateResume && this.state !== 'offline') {
      this.ensureReady(reason);
      return;
    }

    // Never trust a transport that crossed a real background/offline boundary.
    // A plain focus signal while already ready does not recreate the socket.
    const requiresFreshTransport = wasSuspended
      || this.state === 'offline'
      || this.state === 'suspended';
    if (!requiresFreshTransport) {
      this.ensureReady(reason);
      return;
    }

    this.invalidateAuthentication(reason);
    this.setState('recovering', reason);
    if (client?.socket.connected) {
      try { client.disconnect(); } catch { }
    }
    this.clearFreshReconnectTimer();
    this.freshReconnectTimer = setTimeout(() => {
      this.freshReconnectTimer = null;
      this.ensureReady(`${reason}:fresh-transport`);
    }, 0);
  }

  setNetworkOnline(online: boolean, reason = online ? 'network-online' : 'network-offline'): void {
    if (this.stopped) return;
    if (!online) {
      this.invalidateAuthentication(reason);
      this.clearReconnectTimer();
      this.setState('offline', reason);
      const client = this.options.getClient();
      if (client?.socket.connected) {
        try { client.disconnect(); } catch { }
      }
      this.options.onUnavailable?.(this.getSnapshot());
      return;
    }
    if (!this.suspended && this.options.getForeground()) this.resume(reason);
  }

  private scheduleAuthentication(client: SkyChatSocketClient, reason: string, delayMs: number): void {
    if (this.stopped || this.suspended || this.isReady(client)) return;
    this.clearAuthTimer();
    this.authTimer = setTimeout(() => {
      this.authTimer = null;
      if (
        this.stopped
        || this.suspended
        || this.options.getClient() !== client
        || !client.socket.connected
        || !this.options.getForeground()
        || !this.options.getOnline()
        || this.isReady(client)
      ) return;

      const token = this.options.getToken();
      if (!token) {
        this.setState('idle', `${reason}:missing-token`);
        return;
      }

      this.authAttempt += 1;
      this.setState('authenticating', `${reason}:auth-${this.authAttempt}`);
      const authPayload = {
        token,
        deviceKind: this.options.deviceKind,
        deviceSessionId: this.options.deviceSessionId,
        messageSyncV2: true,
        capabilities: { messageSyncV2: true }
      };
      const authEpoch = this.transportEpoch;
      const authSocketId = String(client.socket.id || '');
      try {
        client.emit('auth', authPayload, (...args: unknown[]) => {
          if (
            this.options.getClient() !== client
            || this.transportEpoch !== authEpoch
            || String(client.socket.id || '') !== authSocketId
          ) return;
          const payload = args[0] as Record<string, unknown> | undefined;
          const proof = {
            transportEpoch: authEpoch,
            socketId: String(payload?.socketId || authSocketId)
          };
          if (payload?.ok === true) this.confirmAuthenticated(client.socket, 'auth-callback', proof);
          else if (payload?.ok === false) this.handleAuthError(client.socket, 'auth-callback-error');
        });
      } catch {
        try { client.authenticate(token); } catch { }
      }
      this.scheduleAuthentication(client, reason, this.nextAuthDelay());
    }, Math.max(0, delayMs));
  }

  private scheduleReconnect(reason: string): void {
    if (this.stopped || this.suspended || this.reconnectTimer || !this.options.getOnline() || !this.options.getForeground()) return;
    const base = this.options.reconnectBaseDelayMs ?? 500;
    const max = this.options.reconnectMaxDelayMs ?? 8000;
    const delay = Math.min(max, base * (2 ** Math.min(this.reconnectAttempt, 5)));
    this.reconnectAttempt += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.stopped || this.suspended || !this.options.getOnline() || !this.options.getForeground()) return;
      const client = this.options.getClient();
      if (!client) return;
      if (!client.socket.connected) {
        try { client.connect(); } catch { }
      } else {
        this.ensureReady(`${reason}:reconnect-auth`);
      }
      if (!this.isReady(client)) this.scheduleReconnect(reason);
    }, delay);
  }

  private nextAuthDelay(): number {
    return Math.min(8000, 900 * (2 ** Math.min(Math.max(0, this.authAttempt - 1), 4)));
  }

  private setState(state: MobileConnectionState, reason: string): void {
    const changed = this.state !== state || this.reason !== reason;
    this.state = state;
    this.reason = reason;
    if (changed) this.options.onStateChange?.(this.getSnapshot());
  }

  private clearAuthTimer(): void {
    if (!this.authTimer) return;
    clearTimeout(this.authTimer);
    this.authTimer = null;
  }

  private clearReconnectTimer(): void {
    if (!this.reconnectTimer) return;
    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }

  private clearFreshReconnectTimer(): void {
    if (!this.freshReconnectTimer) return;
    clearTimeout(this.freshReconnectTimer);
    this.freshReconnectTimer = null;
  }

  private clearTimers(): void {
    this.clearAuthTimer();
    this.clearReconnectTimer();
    this.clearFreshReconnectTimer();
  }
}
